import { SESSION_KEYS } from '@/config/constants';
import { DISMISS_STORAGE_KEY_PREFIX } from '@/components/features/PolicyWarningBanner';

/**
 * Checks if auth tokens exist in localStorage (synchronous check).
 * This is useful for determining if we should show a loading state
 * while verifying tokens, rather than immediately redirecting to login.
 *
 * Note: This only checks if tokens exist, not if they're valid/expired.
 * The actual validation happens in checkExistingAuth().
 */
export function hasStoredAuthTokens(): boolean {
  const token = localStorage.getItem(SESSION_KEYS.AUTH_TOKEN);
  const memberUuid = localStorage.getItem(SESSION_KEYS.MEMBER_UUID);
  return !!(token && memberUuid);
}

/**
 * Clears all authentication-related data from storage.
 *
 * This includes:
 * - Auth token (localStorage - persists across tabs)
 * - Member ID (localStorage - persists across tabs)
 * - All cached data (sessionStorage - tab-specific cache)
 *
 * Used when logging out or when authentication fails (401 errors).
 *
 * Note: Auth tokens are stored in localStorage to support "open in new tab"
 * functionality. Cache data remains in sessionStorage since it's tab-specific.
 */
export function clearAuthSession(): void {
  // Auth tokens in localStorage (shared across tabs)
  localStorage.removeItem(SESSION_KEYS.AUTH_TOKEN);
  localStorage.removeItem(SESSION_KEYS.MEMBER_UUID);

  // Clear any member-scoped UI state that lives in localStorage and could
  // otherwise leak across user sessions on a shared browser.
  Object.keys(localStorage).forEach(key => {
    if (key.startsWith(DISMISS_STORAGE_KEY_PREFIX)) {
      localStorage.removeItem(key);
    }
  });

  // Clear all cache entries from sessionStorage (tab-specific)
  Object.keys(sessionStorage).forEach(key => {
    if (key.startsWith(SESSION_KEYS.CACHE_PREFIX)) {
      sessionStorage.removeItem(key);
    }
  });
}
